import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Menu, MenuItem, Tabs } from '@mui/material';
import TabLabel from '@/components/TabLabel/TabLabel';
import {
  campaignTabs, stateTabs, summaryTabs, subCampaignsTabs, helpTextTabs, fieldsTabs, statusCampaign, subCRSummaryTabs, ROUTES, getAbsoluteRoute,
} from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { getVersionUrlParam, hideFeedbackSubCR, isSubCR as utilsIsSubCR, isSummary as utilsIsSummary } from '@/helpers/utils';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import { getCampaignChanges, getCampaignInfo, getCampaignValidations } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { getSubcampaignComments, getSubCampaignInfo, getSubCampaignValidations } from '@/redux/SubCampaign/subCampaign.reducer';
import { CampaignInfo, RouterParams, SubCampaignInfoOverview, SubCampaignNotesAreas, SubCRNotes } from '@/types';
import TabsScroller from './TabsScroller';
import styles from './TabsComponent.module.scss';

interface TabsComponentProps {
  tabSelected: number;
  campaignInfo: CampaignInfo;
  headerLanguage: string;
  campaignValidations: any;
  subCampaignValidations: any;
  isSummary?: boolean;
  isSubCR?: boolean;
  handleNavClick: (path: string) => void;
  isHelp?: boolean;
  isFields?: boolean;
  key?: string;
  campaignChanges: any;
  subCampaignInfo: SubCampaignInfoOverview;
  comments: { id: string; notes: SubCRNotes[] };
}

const notesIconInfo = {
  name: 'icon-klemmbrett-stift',
  tooltip: 'navTab.TAB_WITH_CM_NOTES',
};

const TAB_LABELS = {
  PLANT_FEEDBACK: 'PLANT_FEEDBACK',
  FEEDBACK: 'FEEDBACK',
  TRANSLATIONS: 'TRANSLATIONS',
};

// TODO It seems that there are several conditions that never execute in the tabs code because the conditions are not met.
// Similarly, two filters are being applied to define which tabs should be displayed.
const TabsComponent: React.FC<TabsComponentProps> = (props) => {
  const { tabSelected, campaignInfo, campaignValidations, subCampaignValidations, isSummary = utilsIsSummary(),
    isSubCR = utilsIsSubCR(), handleNavClick, isHelp, isFields, key, campaignChanges, subCampaignInfo, comments } = props;
  const { id, version } = useParams<RouterParams>();
  const { isButtonDisabled } = useCRPermissionMatrix(campaignInfo?.status, campaignInfo?.authorization);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dropdownTab, setDropdownTab] = useState<string>('');
  const tabsWithNotes = comments?.notes?.filter((noteInfo) => noteInfo.notes?.length)?.map((noteInfo) => noteInfo.module);

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>, tab: string) => {
    setAnchorEl(event.currentTarget);
    setDropdownTab(tab);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setDropdownTab('');
  };

  const handleDropdownItemClick = (path: string) => {
    handleNavClick(path);
    handleDropdownClose();
  };

  const hasInvalidForm = (path: string) => {
    let language = campaignInfo?.defaultLanguage;
    const stateTab = stateTabs[path];

    if (path.includes('translations')) {
      language = campaignInfo?.defaultLanguage === 'EN' ? 'DE' : 'EN';
    }
    const isInvalid = (isSubCR ? subCampaignValidations : campaignValidations)?.[stateTab]?.[language]?.invalid || (path.includes('translations') && campaignInfo?.isTranslationUpdateNeeded);

    return isInvalid;
  };

  const hasTabWithChanges = (path) => {
    if (!campaignChanges?.active) return false;
    const stateTab = stateTabs[path];

    return campaignChanges?.[stateTab]?.length > 0;
  };

  type NavTab = {
    label: string;
    link: string;
    isDropdown?: boolean;
    dropdownItems?: { label: string; link: string }[];
  };

  const getTabsToDisplay = (): NavTab[] => {
    if (isFields) {
      return fieldsTabs();
    }

    if (isHelp) {
      const helpTabFiltered = helpTextTabs.filter((tab) => tab.label !== TAB_LABELS.PLANT_FEEDBACK);

      return helpTabFiltered;
    }

    if (isSummary) {
      if (isSubCR) {
        return hideFeedbackSubCR(subCampaignInfo)
          ? subCRSummaryTabs.filter((tab) => tab.label !== TAB_LABELS.PLANT_FEEDBACK)
          : subCRSummaryTabs;
      }

      return summaryTabs;
    }

    if (isSubCR) {
      if (hideFeedbackSubCR(subCampaignInfo)) {
        return subCampaignsTabs.filter((tab) => tab.label !== TAB_LABELS.FEEDBACK);
      }

      return subCampaignsTabs;
    }

    return campaignTabs;
  };

  const getPath = (navTab) => {
    if (isFields) {
      return getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/fields/${navTab.link}`);
    }

    if (isHelp) {
      return getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/help/${navTab.link}`);
    }

    return isSubCR ? getAbsoluteRoute(`${ROUTES.SUB_CR_DETAIL}/${id}/${navTab.link}`) : getAbsoluteRoute(`${ROUTES.CR_DETAIL}/${id}${getVersionUrlParam(version)}/${navTab.link}`);
  };

  const tabs = getTabsToDisplay().filter(
    (navTab) => {
      if (isHelp) return true;

      if ((navTab.label === TAB_LABELS.TRANSLATIONS && (campaignInfo?.activeLanguages?.length <= 1 || isButtonDisabled(CR_PERMISSION_ACTIONS.TRANSLATIONS_TAB)))
        || (isSummary && !isSubCR && navTab.label === TAB_LABELS.PLANT_FEEDBACK && ((![statusCampaign.IN_PLANT_VALIDATION, statusCampaign.IN_VAN_PLANT_VALIDATION].includes(campaignInfo.status) || isButtonDisabled(CR_PERMISSION_ACTIONS.PLANT_FEEDBACK_TAB))
          || (isSubCR && hideFeedbackSubCR(subCampaignInfo) && [TAB_LABELS.FEEDBACK, TAB_LABELS.PLANT_FEEDBACK].includes(navTab.label))))
      ) return false;

      return true;
    },
  );

  const invalidTabs = tabs.map((tab, position) => ({ ...tab, position }))
    .filter(({ link }) => hasInvalidForm(link) || hasTabWithChanges(link));

  const displayTabLabel = (navTab) => {
    const { label, link } = navTab;
    const tabHasNotes = tabsWithNotes?.includes(label as SubCampaignNotesAreas);

    return (
      <TabLabel
        invalid={hasInvalidForm(link) || hasTabWithChanges(link)}
        label={label}
        iconInfo={tabHasNotes ? notesIconInfo : undefined}
      />
    );
  };

  return (
    <Tabs
      key={key}
      indicatorColor="primary"
      value={tabSelected}
      variant="scrollable"
      scrollButtons
      ScrollButtonComponent={invalidTabs.length > 0
        ? (nativeProps) => <TabsScroller {...nativeProps} invalidTabs={invalidTabs} />
        : undefined}
      orientation="horizontal"
      allowScrollButtonsMobile
    >
      {tabs.map((navTab) => (
        <div
          id={`tab-${navTab.link}`}
          data-testid={`tab-${navTab.link}`}
          key={navTab.label}
          onClick={() => handleNavClick(getPath(navTab))}
          className={styles.Link}
        >
          {navTab.isDropdown
            ? (
              <>
                <div
                  onClick={(event) => handleDropdownClick(event, navTab.link)}
                  className={styles.DropdownTab}
                >
                  {displayTabLabel(navTab)}
                </div>
                <Menu
                  anchorEl={anchorEl}
                  open={dropdownTab === navTab.link}
                  onClose={handleDropdownClose}
                >
                  {navTab.dropdownItems?.map((item) => (
                    <MenuItem
                      key={item.link}
                      onClick={() => handleDropdownItemClick(item.link)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )
            : (
              <div onClick={() => handleNavClick(navTab.link)}>
                {displayTabLabel(navTab)}
              </div>
            )}
        </div>
      ))}
    </Tabs>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  campaignValidations: getCampaignValidations(state),
  subCampaignValidations: getSubCampaignValidations(state),
  campaignChanges: getCampaignChanges(state),
  subCampaignInfo: getSubCampaignInfo(state),
  comments: getSubcampaignComments(state),
});

export default connect(mapStateToProps)(TabsComponent);
